/**
 * HALO - Team Pulse Dashboard
 * Real-time team wellness monitoring
 * Design: Clean dashboard with stat cards and employee grid
 */

import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  Users,
  AlertTriangle,
  Filter,
  Search,
  Bell,
  TrendingUp,
  TrendingDown,
  Eye,
  Moon,
  X,
  CheckCircle,
} from "lucide-react";

import {
  EmployeeCard,
  TeamPulseGrid,
  type EmployeeWellnessData,
} from "../components/EmployeeCard";
import {
  useTeamPulse,
  useWellnessSummary,
  useFilteredEmployees,
} from "../hooks/useTeamPulse";
import type { WellnessAlertRow } from "../lib/supabase";

// ============================================================================
// DEMO DATA - Empty by default, will load from backend
// ============================================================================

const DEMO_EMPLOYEES: EmployeeWellnessData[] = [];

// ============================================================================
// SECTION CARDS COMPONENT
// ============================================================================

interface SectionCardData {
  title: string;
  value: string | number;
  description: string;
  trend?: {
    value: string;
    direction: "up" | "down";
    isPositive: boolean;
  };
  footer: string;
  icon: React.ReactNode;
}

interface SectionCardsProps {
  cards: SectionCardData[];
}

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardFooter,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const SectionCards: React.FC<SectionCardsProps> = ({ cards }) => {
  return (
    <div className="grid grid-cols-1 gap-4 px-4 sm:grid-cols-2 lg:px-6 xl:grid-cols-4">
      {cards.map((card, idx) => (
        <Card key={idx} className="from-primary/5 to-surface-dark">
          <CardHeader>
            <CardDescription className="flex items-center gap-2">
              {card.icon}
              {card.title}
            </CardDescription>
            <CardTitle className="text-2xl font-semibold tabular-nums">
              {card.value}
            </CardTitle>
            {card.trend && (
              <CardAction>
                <Badge
                  variant={card.trend.isPositive ? "success" : "destructive"}
                >
                  {card.trend.direction === "up" ? (
                    <TrendingUp className="w-3.5 h-3.5" />
                  ) : (
                    <TrendingDown className="w-3.5 h-3.5" />
                  )}
                  {card.trend.value}
                </Badge>
              </CardAction>
            )}
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5 text-sm">
            <div className="line-clamp-1 flex gap-2 font-medium text-foreground">
              {card.description}
              {card.trend && (
                <>
                  {card.trend.direction === "up" ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                </>
              )}
            </div>
            <div className="text-muted-slate text-xs">{card.footer}</div>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

interface AlertPanelProps {
  alerts: WellnessAlertRow[];
  onAcknowledge: (alertId: string) => void;
  onClose: () => void;
}

const AlertPanel: React.FC<AlertPanelProps> = ({
  alerts,
  onAcknowledge,
  onClose,
}) => (
  <motion.div
    className="fixed inset-y-0 right-0 w-96 bg-surface-dark border-l border-glass-grey shadow-2xl z-50 flex flex-col"
    initial={{ x: "100%" }}
    animate={{ x: 0 }}
    exit={{ x: "100%" }}
    transition={{ type: "spring", damping: 25, stiffness: 200 }}
  >
    <div className="flex items-center justify-between p-4 border-b border-glass-grey">
      <h2 className="font-semibold text-foreground flex items-center gap-2">
        <Bell className="w-5 h-5 text-signal-amber" />
        Active Alerts ({alerts.length})
      </h2>
      <button
        onClick={onClose}
        className="p-2 hover:bg-surface-elevated rounded-lg transition-colors"
      >
        <X className="w-5 h-5 text-muted-slate" />
      </button>
    </div>
    <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
      {alerts.length === 0 ? (
        <div className="text-center py-8 text-muted-slate">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No active alerts</p>
        </div>
      ) : (
        alerts.map((alert) => (
          <motion.div
            key={alert.id}
            className={`p-4 rounded-xl border ${
              alert.severity === "critical"
                ? "bg-signal-crimson/10 border-signal-crimson/30"
                : "bg-signal-amber/10 border-signal-amber/30"
            }`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: 50 }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p
                  className={`font-medium ${
                    alert.severity === "critical"
                      ? "text-signal-crimson"
                      : "text-signal-amber"
                  }`}
                >
                  {alert.alert_type
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </p>
                <p className="text-sm text-muted-slate mt-1">{alert.message}</p>
                <p className="text-xs text-muted-slate mt-2">
                  {formatDistanceToNow(new Date(alert.created_at), {
                    addSuffix: true,
                  })}
                </p>
              </div>
              <button
                onClick={() => onAcknowledge(alert.id)}
                className="shrink-0 p-2 hover:bg-surface-elevated rounded-lg transition-colors"
                title="Acknowledge"
              >
                <CheckCircle className="w-5 h-5 text-muted-slate" />
              </button>
            </div>
          </motion.div>
        ))
      )}
    </div>
  </motion.div>
);

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

interface TeamPulsePageProps {
  organizationId?: string;
  userId?: string;
  useDemoData?: boolean;
}

export const TeamPulsePage: React.FC<TeamPulsePageProps> = ({
  organizationId = "demo-org",
  userId = "demo-user",
  useDemoData = false,
}) => {
  const navigate = useNavigate();
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOnlineOnly, setFilterOnlineOnly] = useState(false);
  const [filterHasAlerts, setFilterHasAlerts] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);

  // Use hook or demo data
  const hookData = useTeamPulse({
    organizationId,
    userId,
    enableRealtime: !useDemoData,
  });

  const employees = useDemoData ? DEMO_EMPLOYEES : hookData.employees;
  const alerts = useDemoData ? [] : hookData.alerts;
  const isLoading = useDemoData ? false : hookData.isLoading;

  // Filter employees
  const filteredEmployees = useFilteredEmployees(employees, {
    onlineOnly: filterOnlineOnly,
    hasAlerts: filterHasAlerts,
    searchQuery,
  });

  // Summary stats
  const summary = useWellnessSummary(employees);

  // KPI cards for SectionCards
  const kpiCards = [
    {
      title: "Online Now",
      value: summary.totalOnline || "—",
      description:
        summary.totalOnline > 0
          ? `${summary.totalOnline} team members active`
          : "Waiting for data",
      footer: "Real-time monitoring",
      icon: <Users className="w-4 h-4 text-signal-green" />,
    },
    {
      title: "High Fatigue",
      value: summary.highFatigueCount || "—",
      description:
        summary.highFatigueCount > 0
          ? `${summary.highFatigueCount} employees need rest`
          : "No fatigue alerts",
      footer: "Based on eye tracking data",
      icon: <AlertTriangle className="w-4 h-4 text-signal-crimson" />,
    },
    {
      title: "Avg Posture",
      value:
        summary.averagePosture !== null ? `${summary.averagePosture}%` : "—",
      description:
        summary.averagePosture !== null
          ? summary.averagePosture >= 70
            ? "Good posture health"
            : "Needs improvement"
          : "No data",
      footer: "Posture score average",
      icon: <TrendingUp className="w-4 h-4 text-signal-blue" />,
    },
    {
      title: "Avg Fatigue",
      value:
        summary.averageFatigue !== null
          ? `${Math.round(summary.averageFatigue * 100)}%`
          : "—",
      description:
        summary.averageFatigue !== null
          ? summary.averageFatigue < 0.5
            ? "Low fatigue levels"
            : "Elevated fatigue detected"
          : "No data",
      footer: "Fatigue index average",
      icon: <Moon className="w-4 h-4 text-signal-amber" />,
    },
  ];

  // Get selected employee data
  const selectedEmployeeData = useMemo(
    () => employees.find((e) => e.id === selectedEmployee),
    [employees, selectedEmployee],
  );

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      {/* KPI Section Cards */}
      <SectionCards cards={kpiCards} />

      {/* Main Content */}
      <div className="max-w-7xl w-full mr-auto px-4 sm:px-6 lg:px-8">
        {/* Filters */}
        <section className="mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-slate" />
              <input
                type="text"
                placeholder="Search team members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="halo-input pl-11"
              />
            </div>

            {/* Filter toggles */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilterOnlineOnly(!filterOnlineOnly)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-micro ${
                  filterOnlineOnly
                    ? "bg-signal-green/10 border-signal-green/50 text-signal-green"
                    : "border-glass-grey text-muted-slate hover:text-foreground hover:bg-surface-elevated"
                }`}
              >
                <Eye className="w-4 h-4" />
                Online Only
              </button>
              <button
                onClick={() => setFilterHasAlerts(!filterHasAlerts)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all duration-micro ${
                  filterHasAlerts
                    ? "bg-signal-amber/10 border-signal-amber/50 text-signal-amber"
                    : "border-glass-grey text-muted-slate hover:text-foreground hover:bg-surface-elevated"
                }`}
              >
                <Filter className="w-4 h-4" />
                Has Alerts
              </button>
            </div>

            {/* Results count */}
            <span className="text-sm text-muted-slate">
              Showing {filteredEmployees.length} of {employees.length}
            </span>
          </div>
        </section>

        {/* Team Grid */}
        <section>
          {!isLoading && filteredEmployees.length === 0 ? (
            <div className="text-center py-20 halo-card border-dashed">
              <div className="w-16 h-16 bg-surface-elevated rounded-xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-muted-slate" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-1">
                No employees found
              </h3>
              <p className="text-muted-slate max-w-sm mx-auto">
                {searchQuery
                  ? "No employees match your search criteria."
                  : "Employees will appear here when they start monitoring."}
              </p>
            </div>
          ) : (
            <TeamPulseGrid
              employees={filteredEmployees}
              isLoading={isLoading}
              onViewDetails={setSelectedEmployee}
            />
          )}
        </section>
      </div>

      {/* Alert Panel Overlay */}
      <AnimatePresence>
        {showAlertPanel && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/50 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAlertPanel(false)}
            />
            <AlertPanel
              alerts={alerts}
              onAcknowledge={hookData.acknowledgeAlert}
              onClose={() => setShowAlertPanel(false)}
            />
          </>
        )}
      </AnimatePresence>

      {/* Employee Detail Modal */}
      <AnimatePresence>
        {selectedEmployee && selectedEmployeeData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedEmployee(null)}
            />
            <motion.div
              className="relative w-full max-w-lg bg-surface-dark border border-glass-grey rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              <div className="flex items-center justify-between p-4 border-b border-glass-grey shrink-0">
                <h2 className="font-semibold text-foreground">
                  Employee Details
                </h2>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="p-2 hover:bg-surface-elevated rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-muted-slate" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto custom-scrollbar flex-1">
                <EmployeeCard employee={selectedEmployeeData} />
                <button
                  onClick={() => {
                    setSelectedEmployee(null);
                    navigate(`/employee/${selectedEmployee}`);
                  }}
                  className="w-full mt-4 px-4 py-2.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition-colors"
                >
                  View Full Details →
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeamPulsePage;
